import React, { useCallback, useMemo, useReducer, useRef } from "react";

import { VisualizerSessionInfo } from "../features/DataVisualizer/DataVisualizer";
import { useAppDispatch } from "../redux/hooks";
import { dataExportSlice } from "../redux/slices/dataExportSlice";
import { uploadSessionFormSlice } from "../redux/slices/uploadSessionFormSlice";
import {
  emptyDialogState,
  emptyModalState,
  emptyPlayerFormValues,
  emptyProgressModalState,
  emptyVisualizerInfo,
} from "../shared/Constants";
import {
  AbortControllerInfo,
  DialogState,
  ModalState,
  PlayerFormValues,
  ProgressModalState,
  UploadFile,
} from "../types";
import DialogContext from "./DialogContext";
import ExportContext from "./ExportContext";
import ModalContext from "./ModalContext";
import PlayerContext from "./PlayerContext";
import UIContext from "./UIContext";
import UploadContext from "./UploadContext";
import VisualizerContext from "./VisualizerContext";

interface AppState {
  modalState: ModalState;
  displayBack: boolean;
  uploadProgressState: ProgressModalState;
  exportProgressState: ProgressModalState;
  uploadFiles: UploadFile[];
  abortControllers: AbortControllerInfo[];
  dialogState: DialogState;
  playerIdToRetrieve: string;
  uploadInProgress: boolean;
  exportInProgress: boolean;
  loadingDashboard: boolean;
  dashboardError: boolean;
  playerFormValues: PlayerFormValues;
  isVisualizerModalOpen: boolean;
  visualizerInfo: VisualizerSessionInfo;
}

type AppAction =
  | { type: "SET_MODAL"; payload: ModalState }
  | { type: "SET_DISPLAY_BACK"; payload: boolean }
  | { type: "SET_UPLOAD_PROGRESS"; payload: ProgressModalState }
  | { type: "SET_EXPORT_PROGRESS"; payload: ProgressModalState }
  | { type: "SET_UPLOAD_FILES"; payload: UploadFile[] }
  | { type: "SET_ABORT_CONTROLLERS"; payload: AbortControllerInfo[] }
  | { type: "SET_DIALOG"; payload: DialogState }
  | { type: "SET_PLAYER_ID_TO_RETRIEVE"; payload: string }
  | { type: "SET_UPLOAD_IN_PROGRESS"; payload: boolean }
  | { type: "SET_EXPORT_IN_PROGRESS"; payload: boolean }
  | { type: "SET_LOADING_DASHBOARD"; payload: boolean }
  | { type: "SET_DASHBOARD_ERROR"; payload: boolean }
  | { type: "SET_PLAYER_FORM_VALUES"; payload: PlayerFormValues }
  | { type: "SET_VISUALIZER_MODAL_OPEN"; payload: boolean }
  | { type: "SET_VISUALIZER_INFO"; payload: VisualizerSessionInfo };

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case "SET_MODAL":
      return {
        ...state,
        modalState: action.payload,
      };
    case "SET_DISPLAY_BACK":
      return {
        ...state,
        displayBack: action.payload,
      };
    case "SET_UPLOAD_PROGRESS":
      return {
        ...state,
        uploadProgressState: action.payload,
      };
    case "SET_EXPORT_PROGRESS":
      return {
        ...state,
        exportProgressState: action.payload,
      };
    case "SET_UPLOAD_FILES":
      return {
        ...state,
        uploadFiles: action.payload,
      };
    case "SET_ABORT_CONTROLLERS":
      return {
        ...state,
        abortControllers: action.payload,
      };
    case "SET_DIALOG":
      return {
        ...state,
        dialogState: action.payload,
      };
    case "SET_PLAYER_ID_TO_RETRIEVE":
      return {
        ...state,
        playerIdToRetrieve: action.payload,
      };
    case "SET_UPLOAD_IN_PROGRESS":
      return {
        ...state,
        uploadInProgress: action.payload,
      };
    case "SET_EXPORT_IN_PROGRESS":
      return {
        ...state,
        exportInProgress: action.payload,
      };
    case "SET_LOADING_DASHBOARD":
      return {
        ...state,
        loadingDashboard: action.payload,
      };
    case "SET_DASHBOARD_ERROR":
      return {
        ...state,
        dashboardError: action.payload,
      };
    case "SET_PLAYER_FORM_VALUES":
      return {
        ...state,
        playerFormValues: action.payload,
      };
    case "SET_VISUALIZER_MODAL_OPEN":
      return {
        ...state,
        isVisualizerModalOpen: action.payload,
      };
    case "SET_VISUALIZER_INFO":
      return {
        ...state,
        visualizerInfo: action.payload,
      };
    default:
      // TypeScript ensures exhaustiveness, but we include this for runtime safety
      const exhaustiveCheck: never = action;
      void exhaustiveCheck; // Satisfy linter while maintaining exhaustiveness check
      console.warn(`Unknown action type: ${(action as any).type}`);
      return state;
  }
};

const initialState: AppState = {
  modalState: emptyModalState,
  displayBack: false,
  uploadProgressState: emptyProgressModalState,
  exportProgressState: emptyProgressModalState,
  uploadFiles: [],
  abortControllers: [],
  dialogState: emptyDialogState,
  playerIdToRetrieve: "",
  uploadInProgress: false,
  exportInProgress: false,
  loadingDashboard: false,
  dashboardError: false,
  playerFormValues: emptyPlayerFormValues,
  isVisualizerModalOpen: false,
  visualizerInfo: emptyVisualizerInfo,
};

interface AppProvidersProps {
  children: React.ReactNode;
}

const useModalContext = (
  appState: AppState,
  appDispatch: React.Dispatch<AppAction>,
  reduxDispatch: any,
) => {
  const { modalState } = appState;
  const modalStateRef = useRef(modalState);
  modalStateRef.current = modalState;

  const setModalState = useCallback(
    (value: ModalState | ((prev: ModalState) => ModalState)) => {
      const payload =
        typeof value === "function" ? value(modalStateRef.current) : value;
      appDispatch({ type: "SET_MODAL", payload });
    },
    [appDispatch],
  );

  const resetModalState = useCallback(
    (modalTitle: string) => {
      setModalState(emptyModalState);

      if (modalTitle.includes("Upload")) {
        reduxDispatch(
          uploadSessionFormSlice.actions.clearUploadSessionFormState(),
        );
      } else if (modalTitle.includes("Define Export")) {
        reduxDispatch(dataExportSlice.actions.clearDataExport());
      }
    },
    [setModalState, reduxDispatch],
  );

  return useMemo(
    () => ({
      modalState,
      setModalState,
      resetModalState,
    }),
    [modalState, setModalState, resetModalState],
  );
};

const useUploadContext = (
  appState: AppState,
  appDispatch: React.Dispatch<AppAction>,
) => {
  const {
    uploadProgressState,
    uploadFiles,
    abortControllers,
    uploadInProgress,
  } = appState;
  const abortControllersRef = useRef(abortControllers);
  abortControllersRef.current = abortControllers;

  const setUploadProgressState = useCallback(
    (
      value:
        | ProgressModalState
        | ((prev: ProgressModalState) => ProgressModalState),
    ) => {
      const payload =
        typeof value === "function" ? value(uploadProgressState) : value;
      appDispatch({ type: "SET_UPLOAD_PROGRESS", payload });
    },
    [appDispatch, uploadProgressState],
  );

  const setUploadFiles = useCallback(
    (value: UploadFile[] | ((prev: UploadFile[]) => UploadFile[])) => {
      const payload = typeof value === "function" ? value(uploadFiles) : value;
      appDispatch({ type: "SET_UPLOAD_FILES", payload });
    },
    [appDispatch, uploadFiles],
  );

  const setAbortControllers = useCallback(
    (
      value:
        | AbortControllerInfo[]
        | ((prev: AbortControllerInfo[]) => AbortControllerInfo[]),
    ) => {
      const payload =
        typeof value === "function"
          ? value(abortControllersRef.current)
          : value;
      appDispatch({ type: "SET_ABORT_CONTROLLERS", payload });
    },
    [appDispatch],
  );

  const setUploadInProgress = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const payload =
        typeof value === "function" ? value(uploadInProgress) : value;
      appDispatch({ type: "SET_UPLOAD_IN_PROGRESS", payload });
    },
    [appDispatch, uploadInProgress],
  );

  return useMemo(
    () => ({
      uploadProgressState,
      setUploadProgressState,
      uploadFiles,
      setUploadFiles,
      abortControllers,
      setAbortControllers,
      uploadInProgress,
      setUploadInProgress,
    }),
    [
      uploadProgressState,
      setUploadProgressState,
      uploadFiles,
      setUploadFiles,
      abortControllers,
      setAbortControllers,
      uploadInProgress,
      setUploadInProgress,
    ],
  );
};

const useExportContext = (
  appState: AppState,
  appDispatch: React.Dispatch<AppAction>,
) => {
  const { exportProgressState, exportInProgress } = appState;

  const setExportProgressState = useCallback(
    (
      value:
        | ProgressModalState
        | ((prev: ProgressModalState) => ProgressModalState),
    ) => {
      const payload =
        typeof value === "function" ? value(exportProgressState) : value;
      appDispatch({ type: "SET_EXPORT_PROGRESS", payload });
    },
    [appDispatch, exportProgressState],
  );

  const setExportInProgress = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const payload =
        typeof value === "function" ? value(exportInProgress) : value;
      appDispatch({ type: "SET_EXPORT_IN_PROGRESS", payload });
    },
    [appDispatch, exportInProgress],
  );

  return useMemo(
    () => ({
      exportProgressState,
      setExportProgressState,
      exportInProgress,
      setExportInProgress,
    }),
    [
      exportProgressState,
      setExportProgressState,
      exportInProgress,
      setExportInProgress,
    ],
  );
};

const usePlayerContext = (
  appState: AppState,
  appDispatch: React.Dispatch<AppAction>,
) => {
  const { playerIdToRetrieve, playerFormValues } = appState;

  const setPlayerIdToRetrieve = useCallback(
    (value: string | ((prev: string) => string)) => {
      const payload =
        typeof value === "function" ? value(playerIdToRetrieve) : value;
      appDispatch({ type: "SET_PLAYER_ID_TO_RETRIEVE", payload });
    },
    [appDispatch, playerIdToRetrieve],
  );

  const setPlayerFormValues = useCallback(
    (
      value: PlayerFormValues | ((prev: PlayerFormValues) => PlayerFormValues),
    ) => {
      const payload =
        typeof value === "function" ? value(playerFormValues) : value;
      appDispatch({ type: "SET_PLAYER_FORM_VALUES", payload });
    },
    [appDispatch, playerFormValues],
  );

  return useMemo(
    () => ({
      playerIdToRetrieve,
      setPlayerIdToRetrieve,
      playerFormValues,
      setPlayerFormValues,
    }),
    [
      playerIdToRetrieve,
      setPlayerIdToRetrieve,
      playerFormValues,
      setPlayerFormValues,
    ],
  );
};

const useUIContext = (
  appState: AppState,
  appDispatch: React.Dispatch<AppAction>,
) => {
  const { displayBack, loadingDashboard, dashboardError } = appState;

  const setDisplayBack = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const payload = typeof value === "function" ? value(displayBack) : value;
      appDispatch({ type: "SET_DISPLAY_BACK", payload });
    },
    [appDispatch, displayBack],
  );

  const setLoadingDashboard = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const payload =
        typeof value === "function" ? value(loadingDashboard) : value;
      appDispatch({ type: "SET_LOADING_DASHBOARD", payload });
    },
    [appDispatch, loadingDashboard],
  );

  const setDashboardError = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const payload =
        typeof value === "function" ? value(dashboardError) : value;
      appDispatch({ type: "SET_DASHBOARD_ERROR", payload });
    },
    [appDispatch, dashboardError],
  );

  return useMemo(
    () => ({
      displayBack,
      setDisplayBack,
      loadingDashboard,
      setLoadingDashboard,
      dashboardError,
      setDashboardError,
    }),
    [
      displayBack,
      setDisplayBack,
      loadingDashboard,
      setLoadingDashboard,
      dashboardError,
      setDashboardError,
    ],
  );
};

const useDialogContext = (
  appState: AppState,
  appDispatch: React.Dispatch<AppAction>,
) => {
  const { dialogState } = appState;

  const setDialogState = useCallback(
    (value: DialogState | ((prev: DialogState) => DialogState)) => {
      const payload = typeof value === "function" ? value(dialogState) : value;
      appDispatch({ type: "SET_DIALOG", payload });
    },
    [appDispatch, dialogState],
  );

  return useMemo(
    () => ({
      dialogState,
      setDialogState,
    }),
    [dialogState, setDialogState],
  );
};

const useVisualizerContext = (
  appState: AppState,
  appDispatch: React.Dispatch<AppAction>,
) => {
  const { isVisualizerModalOpen, visualizerInfo } = appState;

  const setIsVisualizerModalOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const payload =
        typeof value === "function" ? value(isVisualizerModalOpen) : value;
      appDispatch({ type: "SET_VISUALIZER_MODAL_OPEN", payload });
    },
    [appDispatch, isVisualizerModalOpen],
  );

  const setVisualizerInfo = useCallback(
    (
      value:
        | VisualizerSessionInfo
        | ((prev: VisualizerSessionInfo) => VisualizerSessionInfo),
    ) => {
      const payload =
        typeof value === "function" ? value(visualizerInfo) : value;
      appDispatch({ type: "SET_VISUALIZER_INFO", payload });
    },
    [appDispatch, visualizerInfo],
  );

  return useMemo(
    () => ({
      isVisualizerModalOpen,
      setIsVisualizerModalOpen,
      visualizerInfo,
      setVisualizerInfo,
    }),
    [
      isVisualizerModalOpen,
      setIsVisualizerModalOpen,
      visualizerInfo,
      setVisualizerInfo,
    ],
  );
};

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const [appState, appDispatch] = useReducer(appReducer, initialState);
  const reduxDispatch = useAppDispatch();

  const modalContextValue = useModalContext(
    appState,
    appDispatch,
    reduxDispatch,
  );
  const uploadContextValue = useUploadContext(appState, appDispatch);
  const exportContextValue = useExportContext(appState, appDispatch);
  const playerContextValue = usePlayerContext(appState, appDispatch);
  const uiContextValue = useUIContext(appState, appDispatch);
  const dialogContextValue = useDialogContext(appState, appDispatch);
  const visualizerContextValue = useVisualizerContext(appState, appDispatch);

  return (
    <ModalContext.Provider value={modalContextValue}>
      <UploadContext.Provider value={uploadContextValue}>
        <ExportContext.Provider value={exportContextValue}>
          <PlayerContext.Provider value={playerContextValue}>
            <UIContext.Provider value={uiContextValue}>
              <DialogContext.Provider value={dialogContextValue}>
                <VisualizerContext.Provider value={visualizerContextValue}>
                  {children}
                </VisualizerContext.Provider>
              </DialogContext.Provider>
            </UIContext.Provider>
          </PlayerContext.Provider>
        </ExportContext.Provider>
      </UploadContext.Provider>
    </ModalContext.Provider>
  );
};
